import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { useMercenary } from '../../../contexts/MercenaryContext';
import Toast from '../../Shared/Toast';
import './BuyOrder.css';

interface Item {
  id: number;
  name: string;
  description: string;
}

interface BuyOrder {
  Item: Item;
  id: number;
  price_offer: number;
  quantity: number;
  expiresAt: string;
}

interface AvailableItem {
  Item: Item;
  price: number;
  quantity: number;
}

const DURATION_OPTIONS = [
  { hours: 72,  label: '3 days',  fee: 5  },
  { hours: 168, label: '7 days',  fee: 10 },
  { hours: 336, label: '14 days', fee: 20 },
];

const BuyOrderForm: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<Item[]>([]);
  const [availableItems, setAvailableItems] = useState<AvailableItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [priceOffer, setPriceOffer] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [duration, setDuration] = useState(72);
  const [activeBuyOrders, setActiveBuyOrders] = useState<BuyOrder[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const { user } = useAuth();
  const { mercenary } = useMercenary();

  /* ── search items ── */
  useEffect(() => {
    if (searchQuery.trim() !== '') {
      axios
        .get(`/api/auction/items/search?query=${searchQuery}`)
        .then((response) => setItems(response.data))
        .catch(() => displayToast('Error fetching items', 'error'));
    } else {
      setItems([]);
    }
  }, [searchQuery]);

  /* ── load market data for selected item ── */
  useEffect(() => {
    if (selectedItem) {
      setAvailableItems([]);
      setActiveBuyOrders([]);

      axios
        .get(`/api/auction/items/available?itemId=${selectedItem.id}`)
        .then((response) => setAvailableItems(response.data))
        .catch(() => displayToast('Error fetching available items', 'error'));

      axios
        .get(`/api/auction/orders/active-bo/${selectedItem.id}`)
        .then((response) => setActiveBuyOrders(response.data))
        .catch(() => displayToast('Error fetching active buy orders', 'error'));
    }
  }, [selectedItem]);

  const handleCreateBuyOrder = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post('/api/auction/buyorder/create', {
        buyer_id: mercenary?.id,
        buyer_user_id: user?.id,
        item_id: selectedItem?.id,
        price_offer: parseInt(priceOffer),
        quantity,
        duration,
      });
      displayToast('Buy order created successfully!', 'success');
      setPriceOffer('');
      setQuantity(1);
      setDuration(24);
      setSelectedItem(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to create buy order';
      displayToast(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  const fee = DURATION_OPTIONS.find((d) => d.hours === duration)?.fee ?? 5;
  const totalCost = priceOffer ? parseInt(priceOffer) * quantity + fee : null;

  return (
    <div className="bo-container">
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          duration={3000}
          onClose={() => setToastMessage(null)}
        />
      )}

      {/* ─── Left: Create Form ─── */}
      <div className="bo-form-panel">
        <h2>Create Buy Order</h2>

        {/* Search */}
        <div className="bo-form-group">
          <label>Search for Items</label>
          <div className="bo-search-wrap">
            <span className="bo-search-icon">🔍</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Enter item name…"
              className="bo-search-input"
            />
          </div>
        </div>

        {/* Search Results */}
        <div className="bo-results-box">
          {items.length > 0 ? (
            <ul className="bo-results-list">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="bo-result-item"
                  onClick={() => {
                    setSelectedItem(item);
                    setSearchQuery('');
                    setItems([]);
                  }}
                >
                  {item.name}
                </li>
              ))}
            </ul>
          ) : (
            <p className="bo-empty-text">
              {searchQuery ? 'No items found' : 'Search results appear here'}
            </p>
          )}
        </div>

        {/* Order Form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleCreateBuyOrder();
          }}
        >
          <div className="bo-form-group">
            <label>Item to Buy</label>
            <input
              type="text"
              value={selectedItem ? selectedItem.name : ''}
              placeholder="Select an item above"
              readOnly
              className="bo-input bo-read-only"
            />
          </div>

          <div className="bo-row-2col">
            <div className="bo-form-group">
              <label>Price Offer</label>
              <input
                type="number"
                value={priceOffer}
                onChange={(e) => setPriceOffer(e.target.value)}
                placeholder="Per item"
                required
                className="bo-input"
              />
            </div>

            <div className="bo-form-group">
              <label>Quantity</label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                min={1}
                required
                className="bo-input"
              />
            </div>
          </div>

          <div className="bo-form-group">
            <label>Duration &amp; Fee</label>
            <div className="bo-duration-row">
              {DURATION_OPTIONS.map((opt) => (
                <button
                  key={opt.hours}
                  type="button"
                  className={`bo-duration-btn${duration === opt.hours ? ' active' : ''}`}
                  onClick={() => setDuration(opt.hours)}
                >
                  <span className="bo-dur-time">{opt.label}</span>
                  <span className="bo-dur-fee">{opt.fee} silver</span>
                </button>
              ))}
            </div>
          </div>

          {/* Summary */}
          {totalCost !== null && (
            <div className="bo-summary">
              <div className="bo-summary-row">
                <span>Items cost</span>
                <span>{parseInt(priceOffer) * quantity} silver</span>
              </div>
              <div className="bo-summary-row">
                <span>Listing fee</span>
                <span>{fee} silver</span>
              </div>
              <div className="bo-summary-row bo-summary-total">
                <span>Total</span>
                <span>{totalCost} silver</span>
              </div>
            </div>
          )}

          <button
            type="submit"
            className="bo-submit-btn"
            disabled={!selectedItem || !priceOffer || submitting}
          >
            {submitting ? 'Placing Order…' : '💰 Place Buy Order'}
          </button>
        </form>
      </div>

      {/* ─── Right: Market Panels ─── */}
      <div className="bo-market-panels">
        <div className="bo-panel">
          <h3>📦 Available Listings</h3>
          {availableItems.length > 0 ? (
            <div className="bo-panel-scroll">
              <table className="bo-panel-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {availableItems.map((item, idx) => (
                    <tr key={idx}>
                      <td className="bo-cell-name">{item.Item.name}</td>
                      <td className="bo-cell-qty">{item.quantity}</td>
                      <td className="bo-cell-price">{item.price} silver</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="bo-empty-text">
              {selectedItem ? 'No listings found' : 'Select an item to view'}
            </p>
          )}
        </div>

        <div className="bo-panel">
          <h3>📋 Active Buy Orders</h3>
          {activeBuyOrders.length > 0 ? (
            <div className="bo-panel-scroll">
              <table className="bo-panel-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Offer</th>
                  </tr>
                </thead>
                <tbody>
                  {activeBuyOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="bo-cell-name">{order.Item.name}</td>
                      <td className="bo-cell-qty">{order.quantity}</td>
                      <td className="bo-cell-price">{order.price_offer} silver</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="bo-empty-text">
              {selectedItem ? 'No buy orders yet' : 'Select an item to view'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuyOrderForm;
